import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/joy';
import JobCard from './JobCard';
import type { Customer } from '../../services/customerService';
import type { Project } from '../../types/project';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';
import {
  copyJobCardSurveyImages,
  createJobCard,
  deleteJobCard,
  listJobAssignees,
  listProjectJobCards,
  loadLatestInstallEvent,
  setJobAssignees,
  listJobSubcontractorIds,
  setJobSubcontractors,
  updateJobCard,
} from './jobCard.service';
import type { JobCardRecord } from './jobCard.types';
import { surveyNotesFrom, withPerCardSurveyNotes } from './jobCardSurveyNotes';
import { cardInstallSpans, installCoverage } from './jobCardCoverage';
import { formatDayRanges, type InstallCalendarEvent } from './jobCardInstallDate';

interface ImplementationStagePanelProps {
  project: Project;
  customer: Customer | null;
  allowAssign?: boolean;
  showSpecLinks?: boolean;
  onAttestedComplete?: () => Promise<void>;
  onReleased?: () => Promise<void>;
  onProjectPatch?: (patch: Partial<Project>) => Promise<void>;
}

const ImplementationStagePanel: React.FC<ImplementationStagePanelProps> = ({
  project,
  customer,
  allowAssign = true,
  showSpecLinks = true,
  onAttestedComplete,
  onReleased,
  onProjectPatch,
}) => {
  const { user } = useSupabaseAuth();
  const [cards, setCards] = useState<JobCardRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [raising, setRaising] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [calendar, setCalendar] = useState<InstallCalendarEvent | null>(null);
  const coverage = useMemo(
    () => installCoverage(calendar, cardInstallSpans(calendar, cards, project)),
    [calendar, cards, project],
  );

  useEffect(() => {
    let cancelled = false;
    const reload = () => {
      loadLatestInstallEvent(project.id)
        .then((next) => { if (!cancelled) setCalendar(next); })
        .catch(() => undefined);
    };
    reload();
    window.addEventListener('focus', reload);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', reload);
    };
  }, [project.id]);

  const loadCards = async () => {
    setLoading(true);
    setError(null);
    try {
      setCards(await listProjectJobCards(project.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load job cards');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  const handleRaise = async () => {
    if (!user?.id || raising) return;
    setRaising(true);
    setError(null);
    try {
      const title = `Job card ${cards.length + 1}`;
      let created = await createJobCard(project.id, title, user.id, { followsBooking: cards.length === 0 });
      const previous = cards[cards.length - 1];
      if (previous) {
        // A new card carries on from the card before it: its survey notes, survey photos and crew.
        // Comments stay on that card; the new card points back to it.
        const notes = surveyNotesFrom(previous, project, cards.length === 1);
        if (notes) created = await updateJobCard(created.id, { survey_notes: notes });
        if (onProjectPatch) {
          await onProjectPatch({
            metadata: withPerCardSurveyNotes(project, created.id, notes),
          });
        }
        const copied = await Promise.allSettled([
          copyJobCardSurveyImages({
            fromJobCardId: previous.id,
            toJobCardId: created.id,
            projectId: project.id,
            copiedBy: user.id,
          }),
          listJobAssignees(previous.id).then((crew) => setJobAssignees(
            created.id,
            project.id,
            crew.map((member) => member.profile_id),
            user.id,
          )),
          listJobSubcontractorIds(previous.id).then((accountIds) => (
            accountIds.length ? setJobSubcontractors(created.id, project.id, accountIds, user.id) : undefined
          )),
        ]);
        const failed = copied.find((result) => result.status === 'rejected') as PromiseRejectedResult | undefined;
        if (failed) {
          setError(`${title} was raised, but not everything copied from ${previous.title}: ${
            failed.reason instanceof Error ? failed.reason.message : 'unknown error'
          }`);
        }
      }
      setCards((current) => [...current, created]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to raise job card');
    } finally {
      setRaising(false);
    }
  };

  const handleDelete = async (jobCardId: string) => {
    await deleteJobCard(jobCardId);
    setCards((current) => current.filter((card) => card.id !== jobCardId));
  };

  return (
    <Stack gap={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        gap={1}
      >
        <Box>
          <Typography level="title-lg">Job cards</Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            Raise as many cards as this workspace needs. Each has its own crew, comments, and completion.
          </Typography>
        </Box>
        {allowAssign && (
          <Button
            onClick={() => { void handleRaise(); }}
            loading={raising}
            disabled={!user?.id}
            sx={{ minHeight: 44, width: { xs: '100%', sm: 'auto' } }}
          >
            Raise job card
          </Button>
        )}
      </Stack>
      {loading && <CircularProgress size="sm" />}
      {error && <Alert color="danger">{error}</Alert>}
      {allowAssign && !loading && (coverage.uncovered.length > 0 || coverage.overlaps.length > 0) && (
        <Alert color="warning" variant="soft">
          <Stack gap={0.5}>
            {coverage.uncovered.length > 0 && (
              <Typography level="body-sm">
                <b>No job card for:</b> {formatDayRanges(coverage.uncovered)}. Raise a card for these days or extend an existing card.
              </Typography>
            )}
            {coverage.overlaps.map((overlap) => (
              <Typography key={overlap.days[0]} level="body-sm">
                <b>{overlap.titles.join(' and ')}</b> both cover {formatDayRanges(overlap.days)}. If this is a crew swap, set the earlier card&apos;s end date.
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}
      {!loading && !error && cards.length === 0 && (
        <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
          No job cards yet. Raise one to assign fitters.
        </Typography>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 2,
          alignItems: 'stretch',
        }}
      >
        {cards.map((card, index) => (
          <JobCard
            key={card.id}
            jobCard={card}
            project={project}
            customer={customer}
            allowAssign={allowAssign}
            showSpecLinks={showSpecLinks}
            onAttestedComplete={onAttestedComplete}
            onReleased={onReleased}
            onProjectPatch={onProjectPatch}
            onJobCardPatch={(next) => {
              setCards((current) => current.map((row) => (row.id === next.id ? next : row)));
            }}
            onDeleteCard={allowAssign ? () => handleDelete(card.id) : undefined}
            isOldest={index === 0}
            continuedFrom={index > 0 ? cards[index - 1].title : null}
            laterCards={cards.slice(index + 1).map((later) => ({ id: later.id, title: later.title }))}
          />
        ))}
      </Box>
    </Stack>
  );
};

export default ImplementationStagePanel;
